// ==========================================================================
// lib/tasks.ts — shared to-do list (FF-033, 9 Oct 2026)
//   028 (10 Oct): tasks can be edited, reassigned and removed; kind 'shopping'
//   is the "Go to the shops" trip, linked to the Kitchen list. Only one trip
//   is open at a time (enforced in the database).
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId, useMe } from "./household";

export type TaskKind = "todo" | "shopping";
export type Task = { id: string; title: string; note: string | null; assignee_id: string | null; due_on: string | null; done_at: string | null; created_by: string | null; created_at: string; kind: TaskKind };

export function useTasks() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["tasks", hid], enabled: !!hid,
    queryFn: async () => {
      const since = new Date(Date.now() - 3 * 864e5).toISOString();
      const { data, error } = await supabase.from("tasks").select("id,title,note,assignee_id,due_on,done_at,created_by,created_at,kind")
        .eq("household_id", hid).is("deleted_at", null).or(`done_at.is.null,done_at.gte.${since}`).order("created_at", { ascending: false });
      if (error) throw error;
      return (data as Task[]).sort((a, b) => Number(!!a.done_at) - Number(!!b.done_at) || (a.due_on ?? "9999").localeCompare(b.due_on ?? "9999"));
    },
  });
}

/** The open "Go to the shops" trip, if there is one. */
export function useShopTrip() {
  const t = useTasks();
  return { ...t, trip: (t.data ?? []).find((x) => x.kind === "shopping" && !x.done_at) ?? null };
}

function useInv() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["tasks", hid] });
}

export function useAddTask() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useInv();
  return useMutation({
    mutationFn: async (t: { title: string; assignee_id: string | null; due_on: string | null; note?: string | null; kind?: TaskKind }) => {
      const { error } = await supabase.from("tasks").insert({ ...t, kind: t.kind ?? "todo", household_id: hid, created_by: me?.id ?? null });
      if (error) throw new Error(error.code === "23505" ? "There's already a shopping trip on. Open it from the list." : error.message);
    },
    onSuccess: inv,
  });
}

export function useUpdateTask() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (a: { id: string; patch: Partial<Pick<Task, "title" | "note" | "assignee_id" | "due_on">> & { remove?: boolean } }) => {
      const { remove, ...rest } = a.patch;
      const row: Record<string, unknown> = { ...rest };
      if (remove) row.deleted_at = new Date().toISOString();
      const { error } = await supabase.from("tasks").update(row).eq("id", a.id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

export function useToggleTask() {
  const me = useMe(); const inv = useInv();
  return useMutation({
    mutationFn: async (t: Task) => {
      const { error } = await supabase.from("tasks").update(t.done_at ? { done_at: null, done_by: null } : { done_at: new Date().toISOString(), done_by: me?.id ?? null }).eq("id", t.id);
      if (error) throw new Error(error.code === "23505" ? "There's already another shopping trip on." : error.message);
    },
    onSuccess: inv,
  });
}
